/* eslint-disable max-lines -- not split yet: duration parsing/formatting, unit conversions and relative-date formatting are separable */
import { i18n } from '@lingui/core';
import {
  DateArg,
  addDays as dateFnsAddDays,
  addMinutes as dateFnsAddMinutes,
  compareAsc as dateFnsCompareAsc,
  compareDesc as dateFnsCompareDesc,
  differenceInDays as dateFnsDifferenceInDays,
  differenceInHours as dateFnsDifferenceInHours,
  differenceInMilliseconds as dateFnsDifferenceInMilliseconds,
  differenceInMinutes as dateFnsDifferenceInMinutes,
  endOfMonth as dateFnsEndOfMonth,
  endOfToday as dateFnsEndOfToday,
  endOfWeek as dateFnsEndOfWeek,
  endOfYear as dateFnsEndOfYear,
  format as dateFnsFormat,
  getDay as dateFnsGetDay,
  getISOWeek as dateFnsGetISOWeek,
  isAfter as dateFnsIsAfter,
  isBefore as dateFnsIsBefore,
  isEqual as dateFnsIsEqual,
  isSameDay as dateFnsIsSameDay,
  isToday as dateFnsIsToday,
  isTomorrow as dateFnsIsTomorrow,
  isYesterday as dateFnsIsYesterday,
  minutesToMilliseconds as dateFnsMinutesToMilliseconds,
  parse as dateFnsParse,
  startOfMonth as dateFnsStartOfMonth,
  startOfToday as dateFnsStartOfToday,
  startOfTomorrow as dateFnsStartOfTomorrow,
  startOfWeek as dateFnsStartOfWeek,
  startOfYear as dateFnsStartOfYear,
  subDays as dateFnsSubDays,
  subMonths as dateFnsSubMonths,
  endOfTomorrow,
  getDate,
  getYear,
  isDate,
  parseISO
} from 'date-fns';
import { enUS, nb, sv } from 'date-fns/locale';
import isNumber from 'lodash/isNumber';
import { pluralize, stringToUpperCase } from '../string/string';

// Predefined date formats for Noddi
export enum NoddiDateFormats {
  TIME = 'HH:mm', // "14:30"
  DATE = 'EEE dd. MMM yyyy', // "Tue 07. Oct 2025"
  DATE_TIME = 'EEE dd. MMM yyyy HH:mm', // "Thu 07. Aug 2025 14:30"
  DATE_TIME_SECONDS = 'EEE dd. MMM yyyy HH:mm:ss', // "Thu 07. Aug 2025 14:30:45"
  API_DATE = 'yyyy-MM-dd' // "2025-10-07"
}

// All available date formats, including Noddi formats
export enum CustomDateFormats {
  MONTH_NAME = 'LLLL', // "January",
  SHORT_MONTH_YEAR = 'MMM yy', // "Oct 25"
  TIME = 'HH:mm', // "14:30"
  FULL_MONTH_DATE_TIME = 'd. MMMM, HH:mm', // "7. October 2025, 14:30"
  FULL_WEEKDAY = 'EEEE', // "Monday"
  WEEKDAY_WITH_NUMBER_AND_MONTH = 'EE dd. MMM', // "Mon 07. Oct"
  SHORT_WEEKDAY_DAY_MONTH_COMMA = 'EEE, d MMM', // "Fri, 12 Jun"
  SHORT_WEEKDAY_DAY = 'EEE d', // "Mon 7"
  SHORT_WEEKDAY = 'EEE', // "Mon"
  NO_DASHED_DATE_ISO_8601_SHORT = 'yyyyMMdd', // "20251007"
  SHORT_WEEKDAY_DAY_MONTH = 'EE. dd.MM', // "Mon. 07.10"
  FULL_WEEKDAY_MONTH_AND_YEAR = 'EEEE, MMMM d, yyyy', // "Monday, October 7, 2025"
  FULL_DAY_MONTH = 'EEEE, d MMMM', // "Monday, 7 October"
  FULL_WEEKDAY_DAY_MONTH = 'EEEE d MMMM', // "Monday 7 October"
  FULL_WEEKDAY_DAY_DOT_MONTH = 'EEEE d. MMMM', // "Monday 7. October" — ordinal dot reads as a date in nb/se
  DAY_OF_MONTH = 'd', // "7"
  DAY_DOT_FULL_MONTH = 'd. MMMM', // "7. October" — ordinal dot reads as a date in nb/se
  DAY_SHORT_MONTH = 'd MMM', // "7 Oct"
  SHORT_DAY = 'dd', // "07"
  SHORT_DAY_MONTH_NUMERIC = 'dd.MM', // "07.10"
  SHORT_MONTH = 'MMM', // "Oct"
  SHORT_DAY_SHORT_MONTH = 'd. MMM', // "7. Oct"
  SHORT_DAY_SHORT_MONTH_YEAR = 'd. MMM yyyy', // "7. Oct 2025"
  SHORT_WEEKDAY_DAY_SHORT_MONTH = 'EEE d MMM', // "Mon 7 Oct"
  SHORT_WEEKDAY_DAY_SHORT_MONTH_YEAR = 'EEE d MMM yyyy', // "Mon 7 Oct 2025"
  HOURS = 'HH', // "14"
  FULL_MONTH = 'MMMM', // "October"
  FULL_MONTH_AND_YEAR = 'MMMM yyyy', // "October 2025"
  FULL_YEAR = 'yyyy', // "2025"
  FULL_DAY_SHORT_MONTH_AND_YEAR = 'EEEE MMM dd', // "Monday Oct 07"
  DAY_MONTH = 'dd MMMM', // "07 October"
  DATE_SHORT_YEAR = 'dd. MMM yy', // "07. Oct 25"
  DATE_SHORT_YEAR_NUMBERS_ONLY = 'dd.MM.yy' // "07.10.25"
}

export type DateFnsDate = DateArg<Date>;

export type MaybeDate = DateFnsDate | null | undefined;

export const INVALID_DATE_PLACEHOLDER = '';

/**
 * Get Day of the week
 * @param date - Date to get day of the week from
 * @returns - Day of the week
 */
export const getDay = (date: MaybeDate) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsGetDay(extractedDate);
  } catch (error) {
    console.error(`getDay: Failed to get day for date: ${date}`, error);

    return undefined;
  }
};

/**
 * Subtract months from a date
 * @param date - Date to subtract months from
 * @param amount - Amount of months to subtract
 * @returns - Date with subtracted months
 */
export const subMonths = (date: MaybeDate, amount: number) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsSubMonths(extractedDate, amount);
  } catch (error) {
    console.error(`subMonths: Failed to sub amount: ${amount} months from date: ${date}`, error);

    return undefined;
  }
};

/**
 * Get end of the year
 * @param date - Date to get end of the year from
 * @returns - End of the year
 */
export const getEndOfYear = (date: MaybeDate) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsEndOfYear(extractedDate);
  } catch (error) {
    console.error(`getEndOfYear: Failed to get end of the year for date: ${date}`, error);

    return undefined;
  }
};

/**
 * Get start of the year
 * @param date - Date to get start of the year from
 * @returns - Start of the year
 */
export const getStartOfYear = (date: MaybeDate) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsStartOfYear(extractedDate);
  } catch (error) {
    console.error(`getStartOfYear: Failed to get start of the year for date: ${date}`, error);

    return undefined;
  }
};

/**
 * Get end of the month
 * @param date - Date to get end of the month from
 * @returns - End of the month
 */
export const getEndOfMonth = (date: MaybeDate) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsEndOfMonth(extractedDate);
  } catch (error) {
    console.error(`getEndOfMonth: Failed to get end of the month for date: ${date}`, error);

    return undefined;
  }
};

/**
 * Get start of the month
 * @param date - Date to get start of the month from
 * @returns - Start of the month
 */
export const getStartOfMonth = (date: MaybeDate) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsStartOfMonth(extractedDate);
  } catch (error) {
    console.error(`startOfMonth: Failed to get start of the month for date: ${date}`, error);

    return undefined;
  }
};

/**
 * Extract date from string, number or date
 * @param date - Date to extract
 * @returns - Extracted date
 */
export const extractDate = (date: MaybeDate) => {
  try {
    // eslint-disable-next-line no-restricted-syntax -- assigned per input type inside the try
    let parsedDate: Date | null | undefined = undefined;

    if (typeof date === 'string') {
      parsedDate = parseISO(date);
    } else if (isNumber(date)) {
      parsedDate = new Date(date);
    } else if (isDate(date)) {
      parsedDate = date;
    }

    return parsedDate;
  } catch (error) {
    throw new Error(`extractDate: Failed to extract date from date: ${date}`, { cause: error });
  }
};

/**
 * Convert i18n locale to date-fns locale
 * @returns - Date-fns locale
 */
export const i18nLocaleToDateFnsLocale = () => {
  switch (i18n.locale) {
    case 'en':
      return enUS;
    case 'se':
      return sv;
    case 'nb':
    default:
      return nb;
  }
};

/**
 * Format date to string
 * @param date - Date to format
 * @param format - Format to use
 * @returns - Formatted date
 */
export const noddiFormatDate = (date: MaybeDate, format: CustomDateFormats | NoddiDateFormats): string => {
  try {
    if (!date) {
      return INVALID_DATE_PLACEHOLDER;
    }

    const parsedDate = extractDate(date);

    if (!parsedDate) {
      return INVALID_DATE_PLACEHOLDER;
    }

    return dateFnsFormat(parsedDate, format, { locale: i18nLocaleToDateFnsLocale() ?? nb });
  } catch (error) {
    console.error('format: Failed to format date', { format, error });
    return INVALID_DATE_PLACEHOLDER;
  }
};

export type Operator = 'isAfter' | 'isBefore' | 'areEqual';

/**
 * Compare dates
 * @param dateLeft
 * @param dateRight
 * @param operator
 * @returns - True if operator is true
 */
export const compareDates = (dateLeft: MaybeDate, dateRight: MaybeDate, operator: Operator) => {
  try {
    const extractedDateLeft = extractDate(dateLeft);
    const extractedDateRight = extractDate(dateRight);

    if (cannotCompareDates(extractedDateLeft ?? null, extractedDateRight ?? null)) {
      return false;
    }

    // eslint-disable-next-line no-restricted-syntax -- picked by the switch on operator
    let operation: undefined | ((date: number | Date, date2: number | Date) => boolean);
    switch (operator) {
      case 'isAfter':
        operation = dateFnsIsAfter;
        break;
      case 'isBefore':
        operation = dateFnsIsBefore;
        break;
      case 'areEqual':
        operation = dateFnsIsEqual;
        break;
      default:
        return false;
    }

    return operation(extractedDateLeft!, extractedDateRight!);
  } catch (error) {
    console.error(`${operator}: Failed to compare dates for dateLeft: ${dateLeft} and dateRight: ${dateRight}`, error);

    return false;
  }
};

/**
 * Add minutes to a date
 * @param date - Date to add minutes to
 * @param amount - Amount of minutes to add
 * @returns - Date with added minutes
 */
export const addMinutes = (date: MaybeDate, amount: number) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsAddMinutes(extractedDate, amount);
  } catch (error) {
    console.error(`addMinutes: Failed to add amount: ${amount} minutes to date: ${date}`, error);

    return undefined;
  }
};

/**
 * Add days to a date
 * @param date - Date to add days to
 * @param amount - Amount of days to add
 * @returns - Date with added days
 */
export const addDays = (date: MaybeDate, amount: number) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsAddDays(extractedDate, amount);
  } catch (error) {
    console.error(`addDays: Failed to add amount: ${amount} days to date: ${date}!`, error);

    return undefined;
  }
};

/**
 * Inclusive calendar days from API date strings (YYYY-MM-DD).
 * Uses noon local time to avoid DST edge flips when iterating.
 */
export const datesInRangeInclusive = (dateFrom: string, dateTo: string): string[] => {
  if (!dateFrom || !dateTo) {
    return [];
  }
  const start = new Date(`${dateFrom}T12:00:00`);
  const end = new Date(`${dateTo}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return [];
  }
  const dates: string[] = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(noddiFormatDate(current, NoddiDateFormats.API_DATE));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

/**
 * Subtract days from a date
 * @param date - Date to subtract days from
 * @param amount - Amount of days to subtract
 * @returns - Date with subtracted days
 */
export const subDays = (date: MaybeDate, amount: number) => {
  try {
    const extractedDate = extractDate(date);

    if (!extractedDate) {
      return undefined;
    }

    return dateFnsSubDays(extractedDate, amount);
  } catch (error) {
    console.error(`subDays: Failed to sub amount: ${amount} days from date: ${date}!`, error);

    return undefined;
  }
};

/**
 * Get start of tomorrow
 * @returns - Start of tomorrow
 */
export const getStartOfTomorrow = () => {
  try {
    return dateFnsStartOfTomorrow();
  } catch (error) {
    console.error('getStartOfTomorrow: Failed to get start of tomorrow', error);

    return new Date();
  }
};

/**
 * Get end of today
 * @returns - End of today
 */
export const getEndOfToday = () => {
  try {
    return dateFnsEndOfToday();
  } catch (error) {
    console.error('getEndOfToday: Failed to get end of today', error);

    return new Date();
  }
};

/**
 * Get start of today
 * @returns - Start of today
 */
export const getStartOfToday = () => {
  try {
    return dateFnsStartOfToday();
  } catch (error) {
    console.error('getStartOfToday: Failed to get start of today', error);

    return new Date();
  }
};

/**
 * Get start of the week
 * @param date - Date to get start of the week from
 * @returns - Start of the week
 */
export const getStartOfWeek = (date: Date) => {
  return dateFnsStartOfWeek(date, { weekStartsOn: 1 });
};

/**
 * Get end of the week
 * @param date - Date to get end of the week from
 * @returns - End of the week
 */
export const getEndOfWeek = (date: Date) => {
  return dateFnsEndOfWeek(date, { weekStartsOn: 1 });
};

/**
 * Parse date from string
 * @param dateString - Date string to parseDate
 * @param format - Format to use
 * @param fallback - Fallback date
 * @returns - Parsed date
 */
export const parseDate = (
  dateString: string | null | undefined,
  format: CustomDateFormats | NoddiDateFormats,
  fallback?: Date
) => {
  try {
    if (!dateString) {
      return fallback ?? new Date();
    }

    return dateFnsParse(dateString, format, new Date());
  } catch (error) {
    console.error(
      `parseDate: Failed to parse dateString: ${dateString} to format: ${format} with fallback: ${fallback}`,
      error
    );

    return new Date();
  }
};
/**
 * Parse meters to kilometers
 * @param value - Value to parseDate
 * @returns - Value in kilometers
 */
export const fromMetersToKilometers = (value: number) => value / 1000;

/**
 * get day name with date string
 * @param dateString - Date string to get day name from
 * @param withYear - Include year in the string
 * @returns - Day name with date string
 */
export const getDayNameAndDateString = (dateString: string, withYear?: boolean) => {
  const dayName = noddiFormatDate(dateString, CustomDateFormats.FULL_WEEKDAY);
  const dateNumber = getDate(dateString);
  const monthName = noddiFormatDate(dateString, CustomDateFormats.MONTH_NAME);
  const year = getYear(new Date(dateString));

  return `${stringToUpperCase(dayName)}, ${dateNumber}.${monthName}${withYear ? ` ${year}` : ''}`;
};

/**
 * Parse seconds to hours
 * @param input - Object with seconds and optional precision (number of decimal places; when omitted returns integer truncated)
 * @returns - Hours
 */
export const secondsToHours = ({ seconds, precision }: { seconds: number; precision?: number }): number => {
  if (precision === undefined) {
    return Math.trunc(seconds / 3600);
  }
  const factor = Math.pow(10, precision);
  return Math.round((seconds / 3600) * factor) / factor;
};

/**
 * Parse seconds to minutes
 * @param input - Object with seconds and optional precision (number of decimal places; when omitted returns integer truncated)
 * @returns - Minutes
 */
export const secondsToMinutes = ({ seconds, precision }: { seconds: number; precision?: number }): number => {
  if (precision === undefined) {
    return Math.trunc(seconds / 60);
  }
  const factor = Math.pow(10, precision);
  return Math.round((seconds / 60) * factor) / factor;
};

/**
 * Parse hours to seconds
 * @param hours - Hours to parseDate
 * @returns - Seconds
 */
export const hoursToSeconds = (hours: number) => hours * 60 * 60;

/**
 * Wall-clock label for an hour of the day given as a decimal (7.5 → "07:30").
 * Timelines and load charts position spans as decimal hours; this renders them.
 */
export const decimalHourToClock = (hour: number): string => {
  const wholeHours = Math.floor(hour);
  const minutes = Math.round((hour - wholeHours) * 60);
  return `${String(wholeHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Parse minutes to seconds
 * @param minutes - Minutes to parseDate
 * @returns - Seconds
 */
export const minutesToSeconds = (minutes: number) => minutes * 60;

/**
 * Parse minutes to milliseconds
 * @param minutes - Minutes to parseDate
 * @returns - Milliseconds
 */
export const minutesToMilliseconds = (minutes: number) => dateFnsMinutesToMilliseconds(minutes);

/**
 * Check if date is today
 * @param date - Date to check
 * @returns - True if date is today
 */
export const isToday = (date: DateFnsDate) => dateFnsIsToday(date);

/**
 * Check if date is tomorrow
 * @param date - Date to check
 * @returns - True if date is tomorrow
 */
export const isTomorrow = (date: DateFnsDate) => dateFnsIsTomorrow(date);

/**
 * Check if date is yesterday
 * @param date - Date to check
 * @returns - True if date is yesterday
 */
export const isYesterday = (date: DateFnsDate) => dateFnsIsYesterday(date);

/**
 * Check if date falls on a calendar day before today (in the past, not today)
 * @param date - Date to check
 * @returns - True if date is before the start of today
 */
export const isPastDay = (date: DateFnsDate) => dateFnsIsBefore(date, dateFnsStartOfToday());

/**
 * Check if two dates fall on the same calendar day
 * @param dateLeft - First date
 * @param dateRight - Second date
 * @returns - True if both dates are on the same day
 */
export const isSameDay = (dateLeft: DateFnsDate, dateRight: DateFnsDate) => dateFnsIsSameDay(dateLeft, dateRight);

/**
 * Gets tomorrow
 * @returns - Returns tomorrow
 */
export const getTomorrow = () => addDays(new Date(), 1) as Date;

/**
 * Get the ISO week number for the given date
 * @param date - Date to read the ISO week number from
 * @returns - ISO 8601 week number (1-53)
 */
export const getISOWeek = (date: DateFnsDate) => dateFnsGetISOWeek(date);

export type SortMode = 'asc' | 'desc';

/**
 * Check if date is invalid
 * @param date
 * @returns - True if date is invalid
 */
export const isDateInvalid = (date: Date) => isNaN(date.getTime());

/**
 * Check if dates for comparison are valid
 * @param dateLeft - Date to compare
 * @param dateRight - Date to compare
 * @returns - True if dates are invalid
 */
export const cannotCompareDates = (dateLeft: Date | null | undefined, dateRight: Date | null | undefined) =>
  !dateLeft || !dateRight || isDateInvalid(dateLeft) || isDateInvalid(dateRight);

/**
 * SortMode compare
 * @param dateLeft - Date to compare
 * @param dateRight - Date to compare
 * @param sort - Sort mode
 * @returns - Comparison result
 */
export const datesSortCompare = (dateLeft: MaybeDate, dateRight: MaybeDate, sort: SortMode) => {
  try {
    const extractedDateLeft = extractDate(dateLeft);
    const extractedDateRight = extractDate(dateRight);

    if (cannotCompareDates(extractedDateLeft ?? null, extractedDateRight ?? null)) {
      return -1;
    }

    // eslint-disable-next-line no-restricted-syntax -- picked by the switch on sort mode
    let operation: undefined | ((dateLeft: Date | number, dateRight: Date | number) => number);
    switch (sort) {
      case 'asc':
        operation = dateFnsCompareAsc;
        break;
      case 'desc':
        operation = dateFnsCompareDesc;
        break;
      default:
        return -1;
    }

    return operation(extractedDateLeft!, extractedDateRight!);
  } catch (error) {
    console.error(
      `compare ${sort}: Failed to compare dateLeft: ${dateLeft} and dateRight: ${dateRight} in ASC mode`,
      error
    );

    return -1;
  }
};

export type TimeUnit = 'days' | 'hours' | 'minutes' | 'milliseconds';

/**
 * Normalize date to midnight
 * @param date - Date to normalize
 * @returns - Normalized date
 * */
const normalizeToMidnight = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Fractional hours between two instants (e.g. 2.5 for 2h30m). Use this for
 * pay/duration display — differenceBetweenDates(.., 'hours') truncates to whole
 * hours via date-fns differenceInHours.
 */
export const decimalHoursBetween = (start: MaybeDate, end: MaybeDate): number => {
  const startDate = extractDate(start);
  const endDate = extractDate(end);
  if (!startDate || !endDate) {
    return 0;
  }
  return (endDate.getTime() - startDate.getTime()) / 3_600_000;
};

/**
 * Get difference between dates, Not sure this works with TzDates?
 * @param dateLeft - Date to compare
 * @param dateRight - Date to compare
 * @param timeUnit -  Time unit
 * @returns - Difference between dates
 */
export const differenceBetweenDates = (dateLeft: MaybeDate, dateRight: MaybeDate, timeUnit: TimeUnit) => {
  const extractedDateLeft = extractDate(dateLeft);
  const extractedDateRight = extractDate(dateRight);

  if (!extractedDateLeft || !extractedDateRight || cannotCompareDates(extractedDateLeft, extractedDateRight)) {
    return -1;
  }

  // If the time unit is days, normalize to midnight
  // eslint-disable-next-line no-restricted-syntax -- picked by the time-unit branches
  let operation: ((dateLeft: Date, dateRight: Date) => number) | undefined;
  if (timeUnit === 'days') {
    operation = (dateLeft, dateRight) =>
      dateFnsDifferenceInDays(normalizeToMidnight(dateLeft), normalizeToMidnight(dateRight));
  } else {
    switch (timeUnit) {
      case 'hours':
        operation = dateFnsDifferenceInHours;
        break;
      case 'minutes':
        operation = dateFnsDifferenceInMinutes;
        break;
      case 'milliseconds':
        operation = dateFnsDifferenceInMilliseconds;
        break;
      default:
        return -1;
    }
  }

  return operation(extractedDateLeft, extractedDateRight);
};

/**
 * Parse seconds to pretty time
 * @param seconds - Seconds to parseDate
 * @returns - Pretty time
 */
export const secondsToPrettyTime = (seconds: number) => {
  if (seconds < 60 * 60) {
    return Math.floor(seconds / 60) + ' min';
  } else {
    return Math.floor(seconds / 60 / 60) + ' h ' + Math.floor((seconds / 60) % 60) + ' m';
  }
};

type Translation = {
  today: string;
  tomorrow: string;
};

/**
 * Get date to `fromNow` format
 * @param date - Date to convert
 * @param translations - Translations
 * @returns - `fromNow` formatted date
 */
export const dateToFromNowDaily = ({ date, translations }: { date: string; translations: Translation }) => {
  const today = getStartOfToday();
  const tomorrow = endOfTomorrow();
  const evaluatedDate = new Date(date);
  if (dateFnsIsSameDay(today, evaluatedDate)) {
    return translations.today;
  } else if (dateFnsIsSameDay(tomorrow, evaluatedDate)) {
    return translations.tomorrow;
  } else {
    return noddiFormatDate(evaluatedDate, CustomDateFormats.WEEKDAY_WITH_NUMBER_AND_MONTH);
  }
};

/**
 * Format a date as a relative day count from today, normalized to local midnight.
 * Examples: "Today", "Tomorrow", "Yesterday", "In 10 days", "10 days ago".
 * @param date - Date to format
 * @returns - Relative day label, or null when the input is missing/invalid
 */
export const formatRelativeDays = (date: MaybeDate): string | null => {
  const target = extractDate(date);
  if (!target || isDateInvalid(target)) {
    return null;
  }
  const diff = dateFnsDifferenceInDays(normalizeToMidnight(target), normalizeToMidnight(new Date()));
  if (diff === 0) {
    return 'Today';
  }
  if (diff === 1) {
    return 'Tomorrow';
  }
  if (diff === -1) {
    return 'Yesterday';
  }
  if (diff > 0) {
    return `In ${diff} ${pluralize(diff, 'day')}`;
  }
  return `${-diff} ${pluralize(-diff, 'day')} ago`;
};

/**
 * Format a date as a relative whole-week count from today, normalized to local midnight.
 * Examples: "Today", "This week", "In 3 weeks", "5 weeks ago".
 * @param date - Date to format
 * @returns - Relative week label, or null when the input is missing/invalid
 */
export const formatRelativeWeeks = (date: MaybeDate): string | null => {
  const target = extractDate(date);
  if (!target || isDateInvalid(target)) {
    return null;
  }
  const diffDays = dateFnsDifferenceInDays(normalizeToMidnight(target), normalizeToMidnight(new Date()));
  const diffWeeks = Math.trunc(diffDays / 7);
  if (diffDays === 0) {
    return 'Today';
  }
  if (diffWeeks === 0) {
    return 'This week';
  }
  if (diffWeeks > 0) {
    return `In ${diffWeeks} ${pluralize(diffWeeks, 'week')}`;
  }
  return `${-diffWeeks} ${pluralize(-diffWeeks, 'week')} ago`;
};

// Same separator as getBookingTimeWindowDisplayText, so booking windows and bare
// instant ranges read alike.
const RANGE_SEPARATOR = ' - ';

/**
 * Compact label for a start/end instant pair: the date once, then a time range
 * ("12. Aug 2026, 09:45 - 10:00"). A span that crosses midnight keeps both dates
 * ("12. Aug 2026 23:30 - 13. Aug 2026 00:30") so the end day is never dropped.
 *
 * Prefer getBookingTimeWindowDisplayText for a delivery window, which carries
 * a timeZone. This one takes bare instants with no timeZone, for sub-hour spans
 * such as stationary bay slots.
 * @param startsAt - Start of the range
 * @param endsAt - End of the range
 * @returns - Formatted range, or an empty string when either input is missing/invalid
 */
export const formatDateTimeRange = (startsAt: MaybeDate, endsAt: MaybeDate): string => {
  const start = extractDate(startsAt);
  const end = extractDate(endsAt);

  if (!start || !end || isDateInvalid(start) || isDateInvalid(end)) {
    return INVALID_DATE_PLACEHOLDER;
  }

  if (!isSameDay(start, end)) {
    const startFormatted = noddiFormatDate(start, NoddiDateFormats.DATE_TIME);
    const endFormatted = noddiFormatDate(end, NoddiDateFormats.DATE_TIME);
    return `${startFormatted}${RANGE_SEPARATOR}${endFormatted}`;
  }

  const dateFormatted = noddiFormatDate(start, NoddiDateFormats.DATE);
  const startTimeFormatted = noddiFormatDate(start, NoddiDateFormats.TIME);
  const endTimeFormatted = noddiFormatDate(end, NoddiDateFormats.TIME);
  return `${dateFormatted}, ${startTimeFormatted}${RANGE_SEPARATOR}${endTimeFormatted}`;
};

/**
 * Parse `HH:mm` format to minutes since midnight
 * @param HHMM - Time in `HH:mm` format
 * @returns - Minutes since midnight
 */
export const HHMMtoMinutesSinceMidnight = (HHMM: string) => {
  const [hours, minutes] = HHMM.split(':');
  if (!hours || !minutes) {
    throw new Error('Invalid time format: ' + HHMM);
  }
  return parseInt(hours) * 60 + parseInt(minutes);
};

type DurationParts = {
  hours: number;
  minutes: number;
  seconds: number;
};

/**
 * Parses ISO 8601 duration (e.g. "PT10H", "PT1H30M") into DurationParts.
 * Only the time part (after T) is used; days are converted to hours (24h = 1 day).
 */
function parseIso8601Duration(durationStr: string): DurationParts {
  // eslint-disable-next-line no-restricted-syntax -- set from the H match, then days are added on
  let hours = 0;
  // eslint-disable-next-line no-restricted-syntax -- set only when the M match is present
  let minutes = 0;
  // eslint-disable-next-line no-restricted-syntax -- set only when the S match is present
  let seconds = 0;

  // Split on T to separate date and time parts — M means months before T, minutes after T
  const timePart = durationStr.split('T')[1] ?? '';
  const datePart = durationStr.split('T')[0] ?? '';

  const hourMatch = timePart.match(/(\d+)H/i);
  const minMatch = timePart.match(/(\d+)M/i);
  // Seconds may be fractional (pydantic timedelta wire shape, e.g. "PT47.999372S") — floor to whole seconds.
  const secMatch = timePart.match(/(\d+(?:\.\d+)?)S/i);

  if (hourMatch) {
    hours = parseInt(hourMatch[1]!, 10);
  }
  if (minMatch) {
    minutes = parseInt(minMatch[1]!, 10);
  }
  if (secMatch) {
    seconds = Math.floor(parseFloat(secMatch[1]!));
  }

  const dayMatch = datePart.match(/(\d+)D/i);
  if (dayMatch) {
    hours += parseInt(dayMatch[1]!, 10) * 24;
  }

  if (hours < 0 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    throw new Error(`Invalid duration values: "${durationStr}".`);
  }

  return { hours, minutes, seconds };
}

/**
 * Parses a duration string into a structured duration object.
 * Accepts HH:MM:SS (e.g. "08:30:00") or ISO 8601 duration (e.g. "PT10H", "PT1H30M").
 * @throws if the format is invalid.
 */
export function parseDuration(durationStr: string): DurationParts {
  if (!durationStr) {
    throw new Error('Duration string is null or empty.');
  }

  if (durationStr.startsWith('P')) {
    return parseIso8601Duration(durationStr);
  }

  const parts = durationStr.split(':');
  if (parts.length !== 3) {
    throw new Error(`Invalid duration format: "${durationStr}". Expected HH:MM:SS or ISO 8601 (e.g. PT10H)`);
  }

  const [hoursStr, minutesStr, secondsStr] = parts;
  const hours = parseInt(hoursStr!, 10);
  const minutes = parseInt(minutesStr!, 10);
  const seconds = parseInt(secondsStr!, 10);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    isNaN(seconds) ||
    hours < 0 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    throw new Error(`Invalid duration values: "${durationStr}".`);
  }

  return { hours, minutes, seconds };
}

/**
 * Formats a structured duration object into a string like "08:30:00".
 */
export function formatDurationAsTime({ hours, minutes, seconds }: DurationParts): string {
  const pad = (num: number) => String(num).padStart(2, '0');

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    !Number.isInteger(seconds) ||
    hours < 0 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    throw new Error(`Invalid duration object: { hours: ${hours}, minutes: ${minutes}, seconds: ${seconds} }`);
  }

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Total decimal hours of a duration string (HH:MM:SS or ISO 8601). "PT8H30M" → 8.5.
 * Returns 0 for null/empty. Use to seed an hours-based form input from a stored
 * duration without losing the sub-hour part — parseDuration(...).hours alone truncates
 * (and ISO normalizes to days, e.g. 40.5h → "P1DT16H30M").
 */
export const durationToHours = (durationStr: string | null | undefined, precision = 2): number => {
  if (!durationStr) {
    return 0;
  }
  const { hours, minutes, seconds } = parseDuration(durationStr);
  return secondsToHours({ seconds: hours * 3600 + minutes * 60 + seconds, precision });
};

/**
 * Whole seconds of a duration string (HH:MM:SS or ISO 8601). "PT5H30M" → 19800.
 * Returns null for null/empty/whitespace AND for any unparsable value, so a
 * caller (e.g. rendering an API-provided remaining-budget string) never crashes
 * on an unexpected wire shape — it just gets null and can hide the value.
 * `parseDuration` stays strict (throws) for callers that want hard validation.
 */
export const durationToSeconds = (durationStr: string | null | undefined): number | null => {
  if (!durationStr?.trim()) {
    return null;
  }
  try {
    const { hours, minutes, seconds } = parseDuration(durationStr);
    return hours * 3600 + minutes * 60 + seconds;
  } catch {
    return null;
  }
};

export const durationToHoursOrNull = (durationStr: string | null | undefined, precision = 2): number | null => {
  const seconds = durationToSeconds(durationStr);
  return seconds === null ? null : secondsToHours({ seconds, precision });
};

/**
 * Formats decimal hours as an "HH:MM:SS" duration string, rounding to the nearest
 * second. 40.5 → "40:30:00". Supports fractional and >24h values, which formatDurationAsTime
 * rejects when fractional hours are passed straight through.
 */
export const hoursToDuration = (hours: number): string => {
  const totalSeconds = Math.round(hoursToSeconds(hours));
  return formatDurationAsTime({
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60
  });
};
